import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { ApiError, api } from '@/api/client';
import type { components } from '@/api/schema';
import { ErrorNote, Field, FormActions, FormGroup, FormPanel, SearchBar, errorMessage } from '@/components/ui';
import { useAuth } from '@/stores/auth';
import { confirmAction, confirmDiscard } from '@/stores/confirm';
import { useUnsavedFlag } from '@/stores/unsaved';
import { ACTION_LABEL, ACTION_ORDER } from './permissionWords';

type RegistryModule = components['schemas']['RegistryModuleDto'];
type RegistryResource = components['schemas']['RegistryResourceDto'];

/** How one cell looks. */
export interface CellState {
  /** Held once the pending changes are saved. */
  held: boolean;
  /** A change waiting to be saved. */
  pending: 'add' | 'remove' | null;
  /** Taken away from this person on their own, though a role gives it. */
  removedForPerson?: boolean;
  /** Why it cannot be given, or null. */
  locked: string | null;
}

/** Why each action needs care, from the registry, if the server sent it. */
function riskOf(res: RegistryResource, action: string): string | null {
  return res.actionDetails?.find((a) => a.action === action)?.risk ?? null;
}

function actionLabel(res: RegistryResource, action: string): string {
  return res.actionDetails?.find((a) => a.action === action)?.label ?? ACTION_LABEL[action] ?? action;
}

export function useRegistry() {
  return useQuery({ queryKey: ['permissions', 'registry'], queryFn: () => api.get('/api/v1/permissions/registry') });
}

/** "Inventory & Stores › Items › Add" for a code. */
export function describeCode(registry: RegistryModule[], code: string): { text: string; risk: string | null } {
  const [m, r, a] = code.split('.');
  const mod = registry.find((x) => x.module === m);
  const res = mod?.resources.find((x) => x.resource === r);
  if (!mod || !res || !a) return { text: code, risk: null };
  return { text: `${mod.label} › ${res.label} › ${actionLabel(res, a)}`, risk: riskOf(res, a) };
}

/**
 * The permission grid (.docs/019): modules down the left with how much of
 * each is held, and one row per thing with ten fixed columns. Cells are real
 * checkboxes; what a cell means is shown by a mark as well as its colour —
 * ✓ held, + will be added, − will be removed, 🔒 cannot be given — and the
 * dangerous ones carry ⚠ with why. One component drives both a role's grid
 * and one person's, as doc 005 always meant.
 */
export function PermissionGrid({
  registry,
  stateOf,
  onSet,
  readOnly,
  rowExtra,
}: {
  registry: RegistryModule[];
  stateOf: (code: string) => CellState;
  /** Sets the codes to held or not. */
  onSet: (codes: string[], held: boolean) => void;
  readOnly: boolean;
  /** Something to show under a row's name, e.g. which roles give it. */
  rowExtra?: (module: string, resource: RegistryResource) => ReactNode;
}) {
  const [search, setSearch] = useState('');
  const [only, setOnly] = useState<'all' | 'held' | 'changes'>('all');
  const [moduleKey, setModuleKey] = useState<string>('all');
  const needle = search.trim().toLowerCase();

  const codesOf = (m: string, res: RegistryResource) => res.actions.map((a) => `${m}.${res.resource}.${a}`);

  const counts = useMemo(
    () =>
      registry.map((mod) => {
        const codes = mod.resources.flatMap((res) => codesOf(mod.module, res));
        const held = codes.filter((c) => stateOf(c).held).length;
        const risky = mod.resources.reduce(
          (n, res) =>
            n + res.actions.filter((a) => stateOf(`${mod.module}.${res.resource}.${a}`).held && riskOf(res, a)).length,
          0,
        );
        return { module: mod.module, label: mod.label, held, total: codes.length, risky };
      }),
    [registry, stateOf],
  );

  const visible = registry
    .filter((mod) => moduleKey === 'all' || mod.module === moduleKey)
    .map((mod) => ({
      ...mod,
      resources: mod.resources.filter((res) => {
        const codes = codesOf(mod.module, res);
        if (only === 'held' && !codes.some((c) => stateOf(c).held)) return false;
        if (only === 'changes' && !codes.some((c) => stateOf(c).pending)) return false;
        if (!needle) return true;
        return (
          res.label.toLowerCase().includes(needle) ||
          mod.label.toLowerCase().includes(needle) ||
          (res.help ?? '').toLowerCase().includes(needle)
        );
      }),
    }))
    .filter((mod) => mod.resources.length > 0);

  // Arrow keys move between cells, as in a spreadsheet; Space ticks.
  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const target = e.target as HTMLElement;
    if (target.tagName !== 'INPUT' || !target.dataset.row) return;
    const row = Number(target.dataset.row);
    const col = Number(target.dataset.col);
    const moves: Record<string, [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    };
    const move = moves[e.key];
    if (!move) return;
    e.preventDefault();
    const grid = e.currentTarget;
    for (let step = 1; step < 40; step += 1) {
      const next = grid.querySelector<HTMLInputElement>(
        `input[data-row="${row + move[0] * step}"][data-col="${col + move[1] * step}"]`,
      );
      if (next) {
        next.focus();
        return;
      }
      if (move[0] !== 0 && !grid.querySelector(`input[data-row="${row + move[0] * step}"]`)) return;
    }
  }

  let rowIndex = 0;

  return (
    // The editors know exactly what is unsaved (a tick put back is no
    // change), so the form's own input-watching guard is told to look away.
    <div className="permgrid" data-unsaved-ignore>
      <nav className="permgrid__nav" aria-label="Modules">
        <button
          type="button"
          className={`permgrid__module ${moduleKey === 'all' ? 'permgrid__module--on' : ''}`}
          aria-pressed={moduleKey === 'all'}
          onClick={() => setModuleKey('all')}
        >
          <span>Everything</span>
        </button>
        {counts.map((c) => (
          <button
            key={c.module}
            type="button"
            className={`permgrid__module ${moduleKey === c.module ? 'permgrid__module--on' : ''}`}
            aria-pressed={moduleKey === c.module}
            onClick={() => setModuleKey(c.module)}
          >
            <span>{c.label}</span>
            <span className="permgrid__count">
              {c.held} of {c.total}
              {c.risky > 0 && (
                <span title={`${c.risky} that need care`}>
                  {' '}
                  · <span aria-hidden="true">⚠</span> {c.risky}
                </span>
              )}
            </span>
          </button>
        ))}
      </nav>

      <div className="permgrid__main">
        <div className="filterbar" data-unsaved-ignore>
          <SearchBar value={search} onChange={setSearch} placeholder="Search, e.g. salary, stock, payroll…" />
          <label className="settings__toggle">
            <input type="radio" name="permgrid-only" checked={only === 'all'} onChange={() => setOnly('all')} />
            <span className="field__label">Everything</span>
          </label>
          <label className="settings__toggle">
            <input type="radio" name="permgrid-only" checked={only === 'held'} onChange={() => setOnly('held')} />
            <span className="field__label">Only what is held</span>
          </label>
          <label className="settings__toggle">
            <input type="radio" name="permgrid-only" checked={only === 'changes'} onChange={() => setOnly('changes')} />
            <span className="field__label">Only my changes</span>
          </label>
        </div>

        <p className="permgrid__legend">
          <span>✓ has it</span> <span>+ will be added</span> <span>− will be taken away</span>{' '}
          <span>🔒 you cannot give it</span> <span>⚠ needs care</span> <span>— does not apply</span>
        </p>

        {visible.length === 0 && <p className="muted">Nothing matches.</p>}

        <div className="tablewrap" onKeyDown={onKeyDown}>
          <table className="permtable">
            <thead>
              <tr>
                <th scope="col">What</th>
                {ACTION_ORDER.map((a) => (
                  <th key={a} scope="col" className="permtable__action">
                    {ACTION_LABEL[a]}
                  </th>
                ))}
              </tr>
            </thead>
            {visible.map((mod) => {
              const moduleCodes = mod.resources.flatMap((res) => codesOf(mod.module, res));
              const givable = moduleCodes.filter((c) => !stateOf(c).locked);
              return (
                <tbody key={mod.module}>
                  <tr className="permtable__modulerow">
                    <th scope="rowgroup" colSpan={ACTION_ORDER.length + 1}>
                      {mod.label}
                      {mod.labelUr && <span className="urdu"> · {mod.labelUr}</span>}
                      {!readOnly && (
                        <span className="permtable__bulk">
                          <button type="button" className="linkbutton" onClick={() => onSet(givable, true)}>
                            Tick all
                          </button>
                          <button type="button" className="linkbutton" onClick={() => onSet(moduleCodes, false)}>
                            Clear all
                          </button>
                        </span>
                      )}
                    </th>
                  </tr>
                  {mod.resources.map((res) => {
                    const r = rowIndex++;
                    const codes = codesOf(mod.module, res);
                    return (
                      <tr key={res.resource}>
                        <th scope="row" className="permtable__what">
                          <span className="permtable__label">
                            {res.label}
                            {res.isField && (
                              <span className="pill" title="Decides whether these figures are shown at all">
                                figures
                              </span>
                            )}
                          </span>
                          {res.help && <span className="permtable__help">{res.help}</span>}
                          {rowExtra?.(mod.module, res)}
                          {!readOnly && (
                            <span className="permtable__bulk">
                              <button
                                type="button"
                                className="linkbutton"
                                onClick={() => onSet(codes.filter((c) => !stateOf(c).locked), true)}
                              >
                                All
                              </button>
                              <button type="button" className="linkbutton" onClick={() => onSet(codes, false)}>
                                None
                              </button>
                            </span>
                          )}
                        </th>
                        {ACTION_ORDER.map((action, c) => {
                          if (!res.actions.includes(action)) {
                            return (
                              <td key={action} className="permtable__na" aria-label="Does not apply">
                                —
                              </td>
                            );
                          }
                          const code = `${mod.module}.${res.resource}.${action}`;
                          const st = stateOf(code);
                          const risk = riskOf(res, action);
                          const label = `${mod.label} › ${res.label}: ${actionLabel(res, action)}`;
                          const glyph = st.pending === 'add' ? '+' : st.pending === 'remove' ? '−' : st.held ? '✓' : st.locked ? '🔒' : '';
                          const stateWords =
                            st.pending === 'add'
                              ? 'will be added'
                              : st.pending === 'remove'
                                ? 'will be taken away'
                                : st.removedForPerson
                                  ? 'taken away from this person'
                                  : st.held
                                    ? 'has it'
                                    : 'does not have it';
                          const title = [stateWords, st.locked, risk].filter(Boolean).join('. ');
                          // A locked cell can still be cleared: taking away is never
                          // an escalation. Only adding it is refused.
                          const disabled = readOnly || (!!st.locked && !st.held);
                          return (
                            <td
                              key={action}
                              className={`permcell2 ${st.held ? 'permcell2--held' : ''} ${
                                st.pending ? `permcell2--${st.pending}` : ''
                              } ${st.removedForPerson ? 'permcell2--removed' : ''}`}
                            >
                              <label title={title}>
                                <input
                                  type="checkbox"
                                  checked={st.held}
                                  disabled={disabled}
                                  data-row={r}
                                  data-col={c}
                                  aria-label={`${label} — ${stateWords}${risk ? `. ${risk}` : ''}`}
                                  onChange={(e) => onSet([code], e.target.checked)}
                                />
                                <span aria-hidden="true" className="permcell2__glyph">
                                  {glyph}
                                </span>
                                {risk && (
                                  <span aria-hidden="true" className="permcell2__risk">
                                    ⚠
                                  </span>
                                )}
                              </label>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              );
            })}
          </table>
        </div>
      </div>
    </div>
  );
}

/**
 * The changes waiting to be saved, in sentences, risky ones first, with the
 * reason the audit log keeps. Saving asks once more, saying how many people
 * the change reaches.
 */
function PendingChanges({
  registry,
  changes,
  reason,
  onReason,
  people,
  extra,
}: {
  registry: RegistryModule[];
  changes: Array<{ code: string; add: boolean }>;
  reason: string;
  onReason: (r: string) => void;
  people: string;
  extra?: ReactNode;
}) {
  const lines = changes
    .map((c) => ({ ...c, ...describeCode(registry, c.code) }))
    .sort((a, b) => Number(!!b.risk) - Number(!!a.risk));
  return (
    <FormGroup title={`${changes.length} change${changes.length === 1 ? '' : 's'} to save — ${people}`} card>
      <ul className="stacklist">
        {lines.map((l) => (
          <li key={l.code}>
            <strong>{l.add ? 'Add' : 'Take away'}:</strong> {l.text}
            {l.risk && (
              <span className="dim">
                {' '}
                — <span aria-hidden="true">⚠</span> {l.risk}
              </span>
            )}
          </li>
        ))}
      </ul>
      {extra}
      <Field
        label="Reason"
        required
        value={reason}
        onChange={onReason}
        placeholder="Why is this change being made? The audit log keeps it."
        span="full"
      />
    </FormGroup>
  );
}

function summaryOf(registry: RegistryModule[], changes: Array<{ code: string; add: boolean }>) {
  const risky = changes.filter((c) => describeCode(registry, c.code).risk);
  const adds = changes.filter((c) => c.add).length;
  const removes = changes.length - adds;
  return [
    adds > 0 ? `${adds} to add` : null,
    removes > 0 ? `${removes} to take away` : null,
    risky.length > 0 ? `${risky.length} that need care` : null,
  ]
    .filter(Boolean)
    .join(', ');
}

// ------------------------------------------------------------------ a role

export function RolePermissionsEditor({
  roleId,
  onCancel,
  onSaved,
}: {
  roleId: string;
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const { user, can } = useAuth();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<Map<string, boolean>>(new Map());
  const [reason, setReason] = useState('');
  const [conflictNote, setConflictNote] = useState<string | null>(null);

  const roleQuery = useQuery({
    queryKey: ['roles', roleId],
    queryFn: () => api.get(`/api/v1/roles/${roleId}` as '/api/v1/roles/{id}'),
  });
  const registry = useRegistry();
  const role = roleQuery.data;
  const held = useMemo(() => new Set(role?.permissions ?? []), [role]);
  const isSelfRole = role ? (user?.roles.includes(role.code) ?? false) : false;
  const canEdit = can('iam.role.update');
  const mine = useMemo(() => new Set(user?.permissions ?? []), [user]);

  const changes = [...pending.entries()].map(([code, add]) => ({ code, add }));
  const dirty = changes.length > 0;
  useUnsavedFlag(dirty);

  const stateOf = useMemo(
    () =>
      (code: string): CellState => {
        const want = pending.get(code);
        const isHeld = want ?? held.has(code);
        const lockedReason =
          isSelfRole && !held.has(code)
            ? 'You hold this role yourself, so you cannot add to it'
            : !mine.has(code)
              ? 'You cannot give this: you do not have it yourself'
              : null;
        return {
          held: isHeld,
          pending: want === undefined ? null : want ? 'add' : 'remove',
          locked: lockedReason,
        };
      },
    [pending, held, isSelfRole, mine],
  );

  function onSet(codes: string[], value: boolean) {
    setPending((prev) => {
      const next = new Map(prev);
      for (const code of codes) {
        if (value && stateOf(code).locked && !held.has(code)) continue;
        if (value === held.has(code)) next.delete(code);
        else next.set(code, value);
      }
      return next;
    });
  }

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/v1/roles/${roleId}/permissions`, {
        changes: changes.map((c) => ({ code: c.code, state: c.add ? 'grant' : 'revoke' })),
        reason: reason.trim(),
        version: role!.version,
      }),
    onSuccess: () => {
      setPending(new Map());
      onSaved(`Permissions for ${role!.nameEn} saved. They reach ${peopleText(role!.userCount)} on their next click.`);
    },
    onError: async (e) => {
      // Someone else saved first: take their version and put these ticks on
      // top, dropping any that are now already the case.
      if (e instanceof ApiError && e.status === 409) {
        const fresh = await queryClient.fetchQuery({
          queryKey: ['roles', roleId],
          queryFn: () => api.get(`/api/v1/roles/${roleId}` as '/api/v1/roles/{id}'),
        });
        const now = new Set(fresh.permissions);
        const redundant: string[] = [];
        setPending((prev) => {
          const next = new Map(prev);
          for (const [code, add] of prev) {
            if (now.has(code) === add) {
              next.delete(code);
              redundant.push(code);
            }
          }
          return next;
        });
        setConflictNote(
          `Someone else changed ${fresh.nameEn} while you were working. Their changes are loaded and yours are kept on top` +
            (redundant.length > 0 && registry.data
              ? `; ${redundant.length} of yours had already been made: ${redundant.map((c) => describeCode(registry.data, c).text).join('; ')}.`
              : '.') +
            ' Check and save again.',
        );
      }
    },
  });

  async function cancel() {
    if (dirty && !(await confirmDiscard())) return;
    onCancel();
  }

  async function submit() {
    if (!role || !registry.data) return;
    const ok = await confirmAction({
      title: `Save changes to ${role.nameEn}?`,
      message: `${summaryOf(registry.data, changes)}. This changes what ${peopleText(role.userCount)} can do.`,
      confirmLabel: 'Save changes',
    });
    if (ok) {
      setConflictNote(null);
      save.mutate();
    }
  }

  return (
    <FormPanel
      title={role ? `What ${role.nameEn} can do` : 'Permissions'}
      intro="Tick what this role may do. Everyone holding the role gets it. Changes are saved together, with a reason."
    >
      {(roleQuery.isPending || registry.isPending) && <p className="muted">Loading…</p>}
      {(roleQuery.isError || registry.isError) && <ErrorNote message="Could not load this role." />}

      {role && registry.data && (
        <div className="stack">
          {isSelfRole && canEdit && (
            <p className="muted">
              You hold this role yourself, so you cannot add to it here — that would widen your own access. You can
              still take things away. Ask another administrator to add anything.
            </p>
          )}
          {!canEdit && <p className="muted">You can look, but only someone with Roles &amp; Permissions access can change this.</p>}
          {conflictNote && <ErrorNote message={conflictNote} />}

          <PermissionGrid registry={registry.data} stateOf={stateOf} onSet={onSet} readOnly={!canEdit} />

          {canEdit && dirty && (
            <PendingChanges
              registry={registry.data}
              changes={changes}
              reason={reason}
              onReason={setReason}
              people={peopleText(role.userCount)}
            />
          )}

          {save.isError && !(save.error instanceof ApiError && save.error.status === 409) && (
            <ErrorNote message={errorMessage(save.error, 'Could not save')} />
          )}

          <FormActions onCancel={() => void cancel()} cancelLabel={dirty ? 'Cancel' : 'Close'}>
            {canEdit && (
              <button
                type="button"
                className="btn btn--primary"
                disabled={!dirty || !reason.trim() || save.isPending}
                onClick={() => void submit()}
              >
                {save.isPending ? 'Saving…' : 'Save changes'}
              </button>
            )}
          </FormActions>
        </div>
      )}
    </FormPanel>
  );
}

function peopleText(n: number): string {
  return n === 1 ? '1 person' : `${n} people`;
}

// ---------------------------------------------------------------- a person

/**
 * One person's own extras and removals, on top of their roles. Ticking is
 * simply against what their roles give: tick something their roles do not
 * give and it becomes an extra; untick something their roles give and it is
 * taken away from them alone; put either back and it follows the roles again.
 */
export function UserPermissionsEditor({
  userId,
  userName,
  version,
  onCancel,
  onDone,
}: {
  userId: string;
  userName: string;
  version: number | undefined;
  onCancel: () => void;
  onDone: () => void;
}) {
  const { user: me, can } = useAuth();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<Map<string, boolean>>(new Map());
  const [reason, setReason] = useState('');
  const [until, setUntil] = useState('');
  const [knownVersion, setKnownVersion] = useState(version);
  const [conflictNote, setConflictNote] = useState<string | null>(null);

  const state = useQuery({
    queryKey: ['users', userId, 'permissions'],
    queryFn: () => api.get(`/api/v1/users/${userId}/permissions` as '/api/v1/users/{id}/permissions'),
  });
  const access = useQuery({
    queryKey: ['users', userId, 'access'],
    queryFn: () => api.get(`/api/v1/users/${userId}/access` as '/api/v1/users/{id}/access'),
  });
  const registry = useRegistry();

  const isSelf = me?.id === userId;
  const canEdit = can('iam.role.update') && !isSelf;
  const mine = useMemo(() => new Set(me?.permissions ?? []), [me]);

  const byCode = useMemo(() => new Map((state.data ?? []).map((p) => [p.code, p])), [state.data]);
  const rolesFor = useMemo(
    () => new Map((access.data?.lines ?? []).map((l) => [l.code, l.fromRoles])),
    [access.data],
  );

  const changes = [...pending.entries()].map(([code, add]) => ({ code, add }));
  const dirty = changes.length > 0;
  useUnsavedFlag(dirty);

  const stateOf = useMemo(
    () =>
      (code: string): CellState => {
        const p = byCode.get(code);
        const effective = p?.effective ?? false;
        const want = pending.get(code);
        return {
          held: want ?? effective,
          pending: want === undefined ? null : want ? 'add' : 'remove',
          removedForPerson: want === undefined && p?.override === 'revoke',
          locked: mine.has(code) ? null : 'You cannot give this: you do not have it yourself',
        };
      },
    [byCode, pending, mine],
  );

  function onSet(codes: string[], value: boolean) {
    setPending((prev) => {
      const next = new Map(prev);
      for (const code of codes) {
        const effective = byCode.get(code)?.effective ?? false;
        if (value && !mine.has(code) && !effective) continue;
        if (value === effective) next.delete(code);
        else next.set(code, value);
      }
      return next;
    });
  }

  /** Each change in the server's words: against the roles, not the screen. */
  function toServer(code: string, add: boolean) {
    const fromRole = byCode.get(code)?.fromRole ?? false;
    const state = add === fromRole ? 'inherit' : add ? 'grant' : 'revoke';
    return { code, state, ...(until && state !== 'inherit' ? { expiresAt: until } : {}) };
  }

  const save = useMutation({
    mutationFn: () =>
      api.patch<{ version: number }>(`/api/v1/users/${userId}/permissions`, {
        changes: changes.map((c) => toServer(c.code, c.add)),
        reason: reason.trim(),
        ...(knownVersion !== undefined ? { version: knownVersion } : {}),
      }),
    onSuccess: (res) => {
      setKnownVersion(res.version);
      setPending(new Map());
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      onDone();
    },
    onError: async (e) => {
      if (e instanceof ApiError && e.status === 409) {
        const fresh = await queryClient.fetchQuery({
          queryKey: ['users', userId, 'permissions'],
          queryFn: () => api.get(`/api/v1/users/${userId}/permissions` as '/api/v1/users/{id}/permissions'),
        });
        const list = await queryClient.fetchQuery({ queryKey: ['users'], queryFn: () => api.get('/api/v1/users') });
        setKnownVersion(list.find((u) => u.id === userId)?.version);
        const now = new Map(fresh.map((p) => [p.code, p.effective]));
        setPending((prev) => {
          const next = new Map(prev);
          for (const [code, add] of prev) if (now.get(code) === add) next.delete(code);
          return next;
        });
        setConflictNote(
          `Someone else changed ${userName}'s access while you were working. Their changes are loaded and yours are kept on top. Check and save again.`,
        );
      }
    },
  });

  async function cancel() {
    if (dirty && !(await confirmDiscard())) return;
    onCancel();
  }

  async function submit() {
    if (!registry.data) return;
    const ok = await confirmAction({
      title: `Save changes to what ${userName} can do?`,
      message: `${summaryOf(registry.data, changes)}.`,
      confirmLabel: 'Save changes',
    });
    if (ok) {
      setConflictNote(null);
      save.mutate();
    }
  }

  const rowExtra = (module: string, res: RegistryResource) => {
    const roles = [
      ...new Set(res.actions.flatMap((a) => rolesFor.get(`${module}.${res.resource}.${a}`) ?? [])),
    ];
    const notes = res.actions
      .map((a) => byCode.get(`${module}.${res.resource}.${a}`))
      .filter((p): p is NonNullable<typeof p> => !!p && p.override !== null);
    if (roles.length === 0 && notes.length === 0) return null;
    return (
      <span className="permtable__why">
        {roles.length > 0 && <span>from {roles.join(', ')}</span>}
        {notes.map((p) => (
          <span key={p.code} className="pill">
            {p.override === 'grant' ? '+' : '−'} {ACTION_LABEL[p.action] ?? p.action}
            {p.overrideBy ? ` by ${p.overrideBy}` : ''}
            {p.reason ? `: ${p.reason}` : ''}
            {p.expiresAt ? ` (until ${new Date(p.expiresAt).toLocaleDateString()})` : ''}
          </span>
        ))}
      </span>
    );
  };

  return (
    <FormPanel
      title={`What ${userName} can do`}
      intro="Their roles decide most of it. Tick to give them something extra, or untick to take something away from them alone."
    >
      {(state.isPending || registry.isPending) && <p className="muted">Loading…</p>}
      {(state.isError || registry.isError) && <ErrorNote message="Could not load permissions." />}

      {state.data && registry.data && (
        <div className="stack">
          {isSelf && (
            <p className="muted">You cannot change your own permissions — this view is read-only. Ask another administrator.</p>
          )}
          {!isSelf && !can('iam.role.update') && (
            <p className="muted">You can look, but only someone with Roles &amp; Permissions access can change it.</p>
          )}
          {conflictNote && <ErrorNote message={conflictNote} />}

          <PermissionGrid
            registry={registry.data}
            stateOf={stateOf}
            onSet={onSet}
            readOnly={!canEdit}
            rowExtra={rowExtra}
          />

          {canEdit && dirty && (
            <PendingChanges
              registry={registry.data}
              changes={changes}
              reason={reason}
              onReason={setReason}
              people={`only ${userName}`}
              extra={
                <label className="field">
                  <span className="field__label">Give until (leave empty for no end)</span>
                  <input
                    className="field__input"
                    type="date"
                    value={until}
                    onChange={(e) => setUntil(e.target.value)}
                  />
                  <span className="settings__hint">
                    Useful for cover: the extra stops by itself after this day.
                  </span>
                </label>
              }
            />
          )}

          {save.isError && !(save.error instanceof ApiError && save.error.status === 409) && (
            <ErrorNote message={errorMessage(save.error, 'Could not save')} />
          )}

          <FormActions onCancel={() => void cancel()} cancelLabel={dirty ? 'Cancel' : 'Close'}>
            {canEdit && (
              <button
                type="button"
                className="btn btn--primary"
                disabled={!dirty || !reason.trim() || save.isPending}
                onClick={() => void submit()}
              >
                {save.isPending ? 'Saving…' : 'Save changes'}
              </button>
            )}
          </FormActions>
        </div>
      )}
    </FormPanel>
  );
}
